import { Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import Breadcrumb from '../components/Breadcrumb'
import { useUserContext } from '@/context/UserContext'
import { Address } from '@/types/quoteTypes'

const validatePostcode = (code: string): boolean => {
  const re = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i
  return re.test(code.replace(/\s/g, ''))
}

const formatPostcode = (code: string): string => {
  const cleaned = code.replace(/\s/g, '').toUpperCase()
  return cleaned.length >= 4 ? cleaned.slice(0, -3) + ' ' + cleaned.slice(-3) : cleaned
}

const QuoteOptions = () => {
  const userContext = useUserContext()
  const postcode = userContext.selectedAddress?.postcode ?? null

  const [isEditing, setIsEditing] = useState(false)
  const [postcodeInput, setPostcodeInput] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [addresses, setAddresses] = useState<Address[]>([])
  const [showAddresses, setShowAddresses] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing) inputRef.current?.focus()
  }, [isEditing])

  useEffect(() => {
    if (searchError) {
      const t = setTimeout(() => setSearchError(''), 5000)
      return () => clearTimeout(t)
    }
  }, [searchError])

  const handleStartEdit = () => {
    setPostcodeInput(postcode ?? '')
    setAddresses([])
    setShowAddresses(false)
    setSearchError('')
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setPostcodeInput('')
    setAddresses([])
    setShowAddresses(false)
    setSearchError('')
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    const formatted = formatPostcode(postcodeInput.trim())
    if (!validatePostcode(formatted)) {
      setSearchError('Please enter a valid UK postcode')
      return
    }
    setIsSearching(true)
    setSearchError('')
    setShowAddresses(false)
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${formatted.replace(/\s/g, '')}`)
      if (!res.ok) throw new Error('Not found')
      const data = await res.json()
      if (data.status === 200 && data.result) {
        setAddresses([{
          id: '1',
          formatted_address: [data.result.admin_district, data.result.region, formatted],
          postcode: formatted
        }])
        setShowAddresses(true)
      } else {
        setSearchError('No results found for this postcode')
      }
    } catch {
      setSearchError('Unable to find this postcode. Please check and try again.')
    } finally {
      setIsSearching(false)
    }
  }

  const handleAddressSelect = (address: Address) => {
    userContext.setSelectedAddress(address)
    setIsEditing(false)
    setPostcodeInput('')
    setAddresses([])
    setShowAddresses(false)
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Get Quote' }
  ]

  const services = [
    {
      id: 'heat-pumps',
      title: 'Air Source Heat Pumps',
      description: 'Efficient all-season heating and cooling for your home',
      grantAmount: '£7,500',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700'
    },
    {
      id: 'solar-panels',
      title: 'Solar Panel Installation',
      description: 'Harness the power of the sun for renewable energy',
      grantAmount: null,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      color: 'from-yellow-500 to-orange-500',
      hoverColor: 'hover:from-yellow-600 hover:to-orange-600'
    }
  ]

  return (
    <div className="min-h-[60vh] bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-dark-background to-dark-plum text-white py-16">
        <div className="container-custom">
          <Breadcrumb
            items={breadcrumbItems}
            className="mb-8"
          />

          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Get Your Free Quote
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Choose the service you're interested in to receive a personalised, no-obligation quote.
              Our experts will provide detailed information tailored to your home and energy needs.
            </p>
          </div>
        </div>
      </section>

      {/* Service Options */}
      <section className="section-padding">
        <div className="container-custom">
          {/* Postcode Section */}
          <div className="mb-10">
            {!isEditing ? (
              <div className="flex items-center gap-4 py-4 px-5 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-primary-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex-grow min-w-0">
                  {postcode ? (
                    <>
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider leading-none mb-1">Building your quote for</p>
                      <p className="text-dark-plum font-bold text-lg leading-tight">{postcode}</p>
                    </>
                  ) : (
                    <p className="text-gray-500 text-sm">No postcode entered — add one to personalise your quote</p>
                  )}
                </div>
                <button
                  onClick={handleStartEdit}
                  className="flex-shrink-0 flex items-center gap-1.5 text-sm font-medium text-primary-green hover:text-primary-green-dark transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-green rounded"
                  aria-label={postcode ? 'Change postcode' : 'Enter postcode'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  {postcode ? 'Change' : 'Enter postcode'}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border-2 border-primary-green shadow-sm p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-dark-plum">
                    {postcode ? 'Update your postcode' : 'Enter your postcode'}
                  </p>
                  <button
                    onClick={handleCancelEdit}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-green rounded"
                    aria-label="Cancel"
                  >
                    Cancel
                  </button>
                </div>

                <form onSubmit={handleSearch} className="flex gap-2">
                  <label htmlFor="postcode-change-input" className="sr-only">Enter postcode</label>
                  <input
                    id="postcode-change-input"
                    ref={inputRef}
                    type="text"
                    value={postcodeInput}
                    onChange={(e) => setPostcodeInput(e.target.value.toUpperCase())}
                    placeholder="e.g. SW1A 1AA"
                    maxLength={8}
                    disabled={isSearching}
                    className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent text-gray-900 placeholder-gray-400 disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={isSearching || !postcodeInput.trim()}
                    className="px-4 py-2 text-sm font-medium bg-primary-green hover:bg-primary-green-dark text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-green focus-visible:ring-offset-2 min-w-[72px] flex items-center justify-center"
                  >
                    {isSearching ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : 'Search'}
                  </button>
                </form>

                {searchError && (
                  <p className="text-sm text-accent-red bg-red-50 px-3 py-2 rounded-lg" role="alert">
                    {searchError}
                  </p>
                )}

                {showAddresses && addresses.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Confirm your area</p>
                    {addresses.map((address) => (
                      <button
                        key={address.id}
                        type="button"
                        onClick={() => handleAddressSelect(address)}
                        className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-primary-green hover:bg-green-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-green transition-all duration-200"
                      >
                        {address.formatted_address.map((line, i) => (
                          <span key={i} className={`block text-sm ${i === 0 ? 'font-semibold text-dark-plum' : 'text-gray-500'}`}>
                            {line}
                          </span>
                        ))}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/quote/${service.id}`}
                className={`group relative bg-gradient-to-br ${service.color} ${service.hoverColor} rounded-2xl p-8 text-white transition-all duration-300 transform hover:scale-105 hover:shadow-2xl w-full sm:w-[calc(50%-1rem)] xl:w-[calc(25%-1.5rem)]`}
              >
                {/* Grant Badge */}
                {service.grantAmount && (
                  <div className="absolute top-6 right-6 bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 py-2">
                    <span className="text-sm font-semibold text-white">
                      {service.grantAmount} Grant
                    </span>
                  </div>
                )}

                {/* Icon */}
                <div className="mb-6 text-white opacity-90 group-hover:opacity-100 transition-opacity">
                  {service.icon}
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-white text-opacity-90 leading-relaxed">
                    {service.description}
                  </p>
                </div>

                {/* Call to Action */}
                <div className="mt-8 flex items-center text-white font-semibold group-hover:translate-x-2 transition-transform">
                  <span>Get Quote</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-white bg-opacity-10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
              </Link>
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-semibold text-dark-plum mb-8">
              Why Choose Gaia Powered?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-dark-plum mb-2">Free No-Obligation Quote</h3>
                <p className="text-gray-600">Get detailed quotes with no hidden costs or commitments</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-dark-plum mb-2">24 Hour Response</h3>
                <p className="text-gray-600">We'll get back to you within 24 hours with your personalised quote</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-dark-plum mb-2">Expert Installation</h3>
                <p className="text-gray-600">Professional installation by certified technicians with full warranty</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default QuoteOptions
